import type { AdapterId, CharacterFactKey } from '../facts';
import type { CharacterFactValues } from '../factValues';
import type { IsoDateString } from './common';
import type { LegacyMilestone } from './tonight';

export type CharacterStatus = 'planned' | 'active' | 'retired';
export const CHARACTER_STATUSES: CharacterStatus[] = ['planned', 'active', 'retired'];

/** The editable roster flags (SPEC §6.1). */
export interface CharacterFlags {
  inRoster: boolean;
  duoOnly: boolean;
  inTonightPool: boolean;
  status: CharacterStatus;
}

export interface CharacterSummary extends CharacterFlags {
  id: string;
  /** First name, as the roster plan writes it. */
  name: string;
  /** "First Last", once observed. */
  fullName: string | null;
  realm: string | null;
  race: string | null;
  /** Class token: DRUID, MAGE… */
  classToken: string | null;
  level: number | null;
  zone: string | null;
  /** Last logout, or null while planned. */
  lastSeenAt: IsoDateString | null;
  /** inRoster && inTonightPool && !duoOnly && status != retired (SPEC §6.4). */
  inPool: boolean;
}

/** `GET /api/characters`, in roster order. */
export interface CharacterListResponse {
  characters: CharacterSummary[];
}

export interface FactEntry<T> {
  value: T;
  source: AdapterId;
  observedAt: IsoDateString;
}

export type CharacterFacts = Partial<{
  [K in CharacterFactKey]: FactEntry<CharacterFactValues[K]>;
}>;

export interface WantedRecipeInfo {
  id: string;
  crafterName: string;
  profession: string;
  specialization: string | null;
  item: string;
  forNames: string[];
  boundToCrafter: boolean;
  spec: string | null;
  note: string | null;
}

/** `GET /api/characters/:id`: the Book page. */
export interface CharacterDetailResponse {
  character: CharacterSummary;
  facts: CharacterFacts;
  /** Wanted recipes this character crafts. */
  wantedRecipes: WantedRecipeInfo[];
  /** Legacy milestones reached ("Mage 25") and the next one to go for. */
  legacy: { reached: string[]; next: LegacyMilestone | null };
  /** `/played` (Alts Forever) and this reset week's sessions; null means no data. */
  played: {
    totalSeconds: number | null;
    totalAsOf: IsoDateString | null;
    thisWeekSeconds: number | null;
  };
}

/** `PATCH /api/characters/:id`. */
export type UpdateCharacterRequest = Partial<CharacterFlags>;

export interface CharacterResponse {
  character: CharacterSummary;
}

// Tonight (SPEC §6.4). The ranked list arrives in M6.

export type FateMode = 'weighted' | 'uniform';

export interface FateRollInfo {
  id: string;
  at: IsoDateString;
  mode: FateMode;
  pickedId: string;
  pickedName: string;
  pool: { id: string; name: string; weight: number }[];
}

/** `POST /api/tonight/roll`. */
export interface RollRequest {
  mode: FateMode;
}

export interface RollResponse {
  roll: FateRollInfo;
}

/** `GET /api/tonight/rolls`: the last five. */
export interface RollListResponse {
  rolls: FateRollInfo[];
}

const CLASS_NAMES: Record<string, string> = {
  DEATHKNIGHT: 'Death Knight',
  DEMONHUNTER: 'Demon Hunter',
};

/** DRUID → Druid, DEATHKNIGHT → Death Knight. */
export function className(token: string | null): string | null {
  if (!token) return null;
  return CLASS_NAMES[token] ?? token.charAt(0) + token.slice(1).toLowerCase();
}
